/* eslint-disable max-lines */
/* eslint-disable require-unicode-regexp */
import {
    BONUS_DENOMINATOR,
    FORCE_RELOAD_GET_PARAM,
    MOBILE_MAX_WIDTH,
    RUNTIME_CSS_VARS,
    SECONDS_PER_HOUR,
    SECONDS_PER_MINUTE,
    TABLET_MAX_WIDTH,
    TABLET_MD_WIDTH,
    TODAY,
} from 'core/constants';
import {EXCEPTION_PATHS} from 'core/modules/routing/constants';
import {readPublicRuntimeEnv} from 'core/helpers/env/runtime';
import {TServerError} from 'core/types';
import {FormikHelpers} from 'formik';
import {FormikValues} from 'formik/dist/types';
import {Dispatch, SetStateAction} from 'react';
import {Bonus, LoyaltyCard, LoyaltyCardBonus} from 'models';
import {checkIsLetter, searchLetterIdx, searchNotLetterIdx} from 'core/regex';

interface IFormatPriceNumberProps {
    options?: Intl.NumberFormatOptions;
    withCurrency?: boolean;
}

export const formatPriceNumber = (value?: string | number, data: IFormatPriceNumberProps = {}) => {
    if (!value) {
        return '';
    }

    const {options = {}, withCurrency = true} = data;

    const result = value
        .toLocaleString('ru-RU', {
            currency: 'RUB',
            maximumFractionDigits: 0,
            ...options,
        })
        .replace(',', '.');

    if (withCurrency) {
        return `${result} ₽`;
    }

    return result;
};

export const formatPrice = (value: string | number, options?: Intl.NumberFormatOptions) => {
    const defaultOptions: Intl.NumberFormatOptions = {
        currency: 'RUB',
        maximumFractionDigits: 0,
        ...options,
    };

    return Number(value).toLocaleString('ru-RU', defaultOptions).replace(',', '.');
};

export const formatPriceToDefault = (value: string, options?: Intl.NumberFormatOptions) => {
    return formatPrice(value, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
        style: 'currency',
        ...options,
    });
};

export const formatLoyaltyCardPoints = (value: LoyaltyCardBonus['points']) => {
    return value.toLocaleString('ru-RU');
};

export const formatLoyaltyCardNumber = (number: LoyaltyCard['number']): string => {
    return number.replace(/\d{2}/gu, (match, offset) => {
        if (2 < offset) {
            return match;
        }
        return `${match} `;
    });
};

export const formatBonus = (bonus?: Bonus) => {
    if (!bonus) {
        return 0;
    }
    return Math.ceil(bonus / BONUS_DENOMINATOR);
};

export const plural = (value: number, words: string[]) => {
    if (0 === value) {
        return words[2];
    }

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const absValue = Math.abs(value) % 100;

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const num = absValue % 10;
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (10 < absValue && 20 > absValue) {
        return words[2];
    }
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (1 < num && 5 > num) {
        return words[1];
    }
    if (1 === num) {
        return words[0];
    }
    return words[2];
};

export const getPluralProducts = (count: number) => {
    return plural(count, ['товар', 'товара', 'товаров']);
};

export const getPluralWineries = (count: number) => {
    return plural(count, ['винотеки', 'винотеках', 'винотеках']);
};

export const isMobileByScreen = () =>
    isBrowserContext() && window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;

export const isTabletByScreen = () =>
    isBrowserContext() && window.matchMedia(`(max-width: ${TABLET_MAX_WIDTH}px)`).matches;

export const isMobileOrTabletByScreen = () =>
    isBrowserContext() && window.matchMedia(`(max-width: ${TABLET_MD_WIDTH}px)`).matches;

export const isDesktopByScreen = () =>
    isBrowserContext() && window.matchMedia(`(min-width: ${TABLET_MD_WIDTH}px)`).matches;

export const isByScreen = (maxWidth: number) =>
    isBrowserContext() && window.matchMedia(`(max-width: ${maxWidth}px)`).matches;

export const getParentPath = (path: string) => {
    const backPath = Boolean(path) && -1 !== path.indexOf('/') ? path.slice(0, path.lastIndexOf('/')) : '/';
    return EXCEPTION_PATHS.includes(backPath) ? '/' : backPath;
};

export const formatBytes = (bytes: number, decimals = 2) => {
    if (!Number(bytes)) {
        return '0 Bytes';
    }

    const k = 1024;
    const dm = 0 > decimals ? 0 : decimals;
    const sizes = ['байт', 'кб', 'мб', 'гб', 'тб', 'пб', 'еб', 'зб', 'йб'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const isBrowserContext = (): boolean => 'undefined' !== typeof window;

const getScrollbarWidth = (): number => {
    if (!isBrowserContext()) {
        return 0;
    }
    return window.innerWidth - document.documentElement.clientWidth;
};

const parsePaddingPx = (value: string): number => {
    if (!value) {
        return 0;
    }
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
};

const APP_ROOT_SELECTOR = '#__next';

const hasFixedAncestor = (el: HTMLElement, root: HTMLElement): boolean => {
    let parent = el.parentElement;
    while (parent && parent !== root) {
        if ('fixed' === window.getComputedStyle(parent).position) {
            return true;
        }
        parent = parent.parentElement;
    }
    return false;
};

const getFixedElementsInAppRoot = (): HTMLElement[] => {
    if (!isBrowserContext()) {
        return [];
    }

    const root = document.querySelector(APP_ROOT_SELECTOR);
    if (!root || !(root instanceof HTMLElement)) {
        return [];
    }

    const fixed: HTMLElement[] = [];

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);

    let node = walker.nextNode();

    while (node && node instanceof HTMLElement) {
        if ('fixed' === window.getComputedStyle(node).position && !hasFixedAncestor(node, root)) {
            fixed.push(node);
        }
        node = walker.nextNode();
    }
    return fixed;
};

let savedBodyPaddingRight = '';
let scrollCompensationAppliedToBody = false;

const fixedElementsPaddingMap = new Map<HTMLElement, string>();

const applyFixedElementsCompensation = (scrollbarWidthPx: number) => {
    const fixed = getFixedElementsInAppRoot();

    for (const el of fixed) {
        const prev = el.style.paddingRight;
        const computed = window.getComputedStyle(el).paddingRight;
        const currentPx = parsePaddingPx(prev || computed);
        fixedElementsPaddingMap.set(el, prev);
        el.style.paddingRight = `${currentPx + scrollbarWidthPx}px`;
    }
};

const removeFixedElementsCompensation = () => {
    for (const [el, saved] of fixedElementsPaddingMap) {
        if (el.isConnected) {
            el.style.paddingRight = saved;
        }
    }
    fixedElementsPaddingMap.clear();
};

export const disableScroll = () => {
    if (!isBrowserContext()) {
        return;
    }

    const body = document.body;

    if (!body) {
        return;
    }
    const scrollbarWidth = getScrollbarWidth();

    body.classList.add('scroll-disabled');

    if (0 < scrollbarWidth) {
        savedBodyPaddingRight = body.style.paddingRight;
        body.style.paddingRight = `${scrollbarWidth}px`;
        scrollCompensationAppliedToBody = true;
        applyFixedElementsCompensation(scrollbarWidth);
    }
};

export const enableScroll = () => {
    if (!isBrowserContext()) {
        return;
    }

    const body = document.body;

    if (!body) {
        return;
    }

    body.classList.remove('scroll-disabled');

    if (scrollCompensationAppliedToBody) {
        body.style.paddingRight = savedBodyPaddingRight;
        scrollCompensationAppliedToBody = false;
        removeFixedElementsCompensation();
    }
};

let savedHtmlPaddingRight = '';
let scrollCompensationAppliedToHtml = false;

export const disableScrollOnHtmlNode = () => {
    if (!isBrowserContext()) {
        return;
    }

    const html = document.documentElement;

    if (!html) {
        return;
    }

    const scrollbarWidth = getScrollbarWidth();

    html.classList.add('scroll-disabled');

    if (0 < scrollbarWidth) {
        savedHtmlPaddingRight = html.style.paddingRight;
        html.style.paddingRight = `${scrollbarWidth}px`;
        scrollCompensationAppliedToHtml = true;
    }
};

export const enableScrollOnHtmlNode = () => {
    if (!isBrowserContext()) {
        return;
    }

    const html = document.documentElement;

    if (!html) {
        return;
    }

    html.classList.remove('scroll-disabled');

    if (scrollCompensationAppliedToHtml) {
        html.style.paddingRight = savedHtmlPaddingRight;
        scrollCompensationAppliedToHtml = false;
    }
};

export const isFractionalNumber = (number: number): boolean => 0 !== number % 1;

export const stripHtml = (html: string): string => {
    return html.replace(/(<([^>]+)>)/giu, '');
};

type TCalcOpenCollapseProps = {
    listContainer: HTMLUListElement | null;
    sliceEnd: number;
};

// Расчет минимальной высоты, если задано требование отображать фиксированное кол-во элементов с учетом кол-ва строк у элементов списка и добавление скролла
export const makeScrollListStyles = ({listContainer, sliceEnd}: TCalcOpenCollapseProps) => {
    if (listContainer) {
        // Создание единого списка элементов. Длина списка зависит от требования к кол-ву отображаемых элементов при раскрытом списке
        const listItems = Array.from(listContainer.querySelectorAll('li'));
        const visibleFilterItemsArr = listItems.slice(0, sliceEnd);

        if (visibleFilterItemsArr.length && listContainer && listContainer) {
            // Подсчет высоты всех отображаемых элементов, высота = высота элемента + верхний отступ
            const totalHeight = visibleFilterItemsArr.reduce((acc, item) => {
                const itemStyle = window.getComputedStyle(item);
                return acc + (item.getBoundingClientRect().height + parseInt(itemStyle.marginTop, 10));
            }, 0);

            // Добавление стилей для скролла и минимальной высоты, для того, чтобы создать скролящийся список
            listContainer.style.maxHeight = `${totalHeight}px`;
            listContainer.style.overflowY = 'auto';
            listContainer.style.paddingRight = '12px';
        }
    }
};

export const formatDeliveryDate = (dateString: string) => {
    const date = new Date(dateString).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
    });

    const today = new Date().toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
    });

    return today === date ? TODAY : date;
};

export const formatEmail = (email = ''): string => {
    const splitEmail = email.split('');

    return splitEmail
        ?.filter((letter) => ' ' !== letter && '+' !== letter && ' ' !== letter && '(' !== letter && ')' !== letter)
        .join('');
};

export const setFormikErrorsToOneField = (
    field: string,
    errors: TServerError[] | TServerError,
    setErrors: FormikHelpers<FormikValues>['setErrors'] | Dispatch<SetStateAction<Record<string, string> | undefined>>,
) => {
    if (Array.isArray(errors)) {
        setErrors({[field]: errors.map((err) => err.message).join(' ')});
        return;
    }

    setErrors({[field]: errors.message});
};

// setCssVar<false> -- любое имя переменной
interface ISetCssVar<Strict extends boolean> {
    cssVarName: Strict extends true ? (typeof RUNTIME_CSS_VARS)[keyof typeof RUNTIME_CSS_VARS] : string;
    unit: string;
    value: string | number;
}

export const setCssVar = <Strict extends boolean = true>({cssVarName, unit, value}: ISetCssVar<Strict>): void => {
    if (!isBrowserContext()) {
        return;
    }

    requestAnimationFrame(() => {
        document.documentElement.style.setProperty(cssVarName, `${value}${unit}`);
    });
};

export const keyBy = <T extends Record<string, any>, E extends boolean = false>(
    objectList: T[],
    key: Extract<keyof T, string>,
    extractionCb?: (object: T) => T[Extract<keyof T, string>],
): Record<string, E extends false ? T : T[Extract<keyof T, string>]> => {
    return objectList.reduce((acc, currentValue) => {
        return {
            ...acc,
            [currentValue[key]]: extractionCb?.(currentValue) || currentValue,
        };
    }, {});
};

export const getArrayLikeErrorFieldData = <E extends Record<string, any>>(
    errors: Record<string, string | undefined>,
    field: keyof E,
): string | undefined => {
    const firstKey = Object.keys(errors).find((key) => new RegExp(`${String(field)}((\\[\\d+\\])|$)`, 'ug').test(key));

    return errors[firstKey || ''];
};

/**
 * https://stackoverflow.com/questions/62368109/update-router-query-without-firing-page-change-event-in-next-js
 * Ну просто роутер некстовый реагирует на изменение даже гет-параметров
 * @param {string} nextUrl
 */
export const isUrlActuallyChange = (nextUrl: string): boolean => {
    const fullUrl = `${window.location.protocol}//${window.location.host}${nextUrl}`;
    const {searchParams} = new URL(fullUrl);

    const newPathName = getUrlPathname(nextUrl);

    return window.location.pathname !== newPathName || Boolean(searchParams.get(FORCE_RELOAD_GET_PARAM));
};

export const getUrlPathname = (url: string): string => {
    const [pathname] = url.split('?');

    return pathname;
};

export const parseTimer = (seconds = 0) => {
    const hours = Math.floor(seconds / SECONDS_PER_HOUR);
    const minutes = Math.floor((seconds - hours * SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
    const secondsLeft = seconds - minutes * SECONDS_PER_MINUTE - hours * SECONDS_PER_HOUR;

    let finalMinutes = '';
    let finalSeconds = '';

    if (0 === minutes) {
        finalMinutes = '00';
    }

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (10 > minutes) {
        finalMinutes = `0${minutes}`;
    }

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (10 <= minutes) {
        finalMinutes = `${minutes}`;
    }

    if (0 === secondsLeft) {
        finalSeconds = '00';
    }

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (10 > secondsLeft) {
        finalSeconds = `0${secondsLeft}`;
    }

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (10 <= secondsLeft) {
        finalSeconds = `${secondsLeft}`;
    }

    return hours ? `${hours}:${finalMinutes}:${finalSeconds}` : `${finalMinutes}:${finalSeconds}`;
};

export const findWordEnd = ({text, searchIndex}: {text: string; searchIndex: number}) => {
    const findSymbol = text[searchIndex];
    const textPart = text.slice(searchIndex);

    if (checkIsLetter(findSymbol)) {
        return searchNotLetterIdx(textPart) + searchIndex;
    }

    const firstLetterIdx = searchLetterIdx(textPart);

    return searchNotLetterIdx(textPart.slice(firstLetterIdx)) + firstLetterIdx + searchIndex;
};

export const addToComaList = (input: string | null, ...items: any[]): string => {
    if (!input) {
        return items.join(',');
    }

    const inputItems = [...input.split(','), ...items];

    return inputItems.join(',');
};

export const deleteFromComaList = (input: string | null, item: string): string => {
    if (!input) {
        return '';
    }

    return input
        .split(',')
        .filter((id) => id !== item)
        .join(',');
};

export const isPage = (pageRoute: string, url: string): boolean => {
    const regex = new RegExp(`^${pageRoute}`, 'ug');

    return regex.test(url);
};

export const isWebview = () => {
    if (!isBrowserContext()) {
        return false;
    }

    const rules = [
        'WebView',
        '(iPhone|iPod|iPad)(?!.*Safari)',
        'Android.*Version/[0-9].[0-9]',
        'Android.*wv',
        'Linux; U; Android',
    ];

    const webviewRegExp = new RegExp('(' + rules.join('|') + ')', 'igu');

    return Boolean(window?.navigator?.userAgent?.match(webviewRegExp));
};

export const getOnlyNumbersFromString = (string: string) => {
    return string.replace(/\D/gu, '');
};

export const getLocaleNumber = (value?: number | null, unit?: string) => {
    if (value === undefined || null === value) {
        return '';
    }

    const unitString = unit ? ` ${unit}` : '';

    return value.toLocaleString('ru-RU') + unitString;
};

export const getPrice = (value?: number | null) => {
    if (!value) {
        return '';
    }

    return `${value.toLocaleString('ru-RU')} ₽`;
};

export const isNumericString = (value: string) => {
    return /^\d*\.?\d*$/iu.test(value);
};

export const getWindowInnerWidth = () => {
    if ('undefined' !== typeof window) {
        return window.innerWidth;
    }

    return 0;
};

export const getWindowInnerHeight = () => {
    if ('undefined' !== typeof window) {
        return window.innerHeight;
    }

    return 0;
};

export const formatNumber = (value: number) => {
    return value.toLocaleString('ru-RU');
};

/**
 * Форматирует рейтинг, добавляя ".0" к целым числам
 * @param rating - значение рейтинга
 * @returns отформатированное значение рейтинга вида 0.0, 1.0, 2.0 и т.д.
 */
export const formatRating = (rating: number): string => {
    // без дробной части?
    return Number.isInteger(rating) ? `${rating}.0` : rating.toString();
};

export const getFlocktoryInstance = () => {
    if ('undefined' === typeof window) {
        return [];
    }

    const flocktory: any[] = (window as any).flocktory || [];
    (window as any).flocktory = flocktory;

    return flocktory;
};

export const isStorybook = (): boolean => {
    // Проверяем переменные окружения
    if ('undefined' !== typeof process) {
        if ('true' === process.env.STORYBOOK) {
            return true;
        }
        if ('true' === readPublicRuntimeEnv('NEXT_PUBLIC_STORYBOOK')) {
            return true;
        }
    }

    // Проверяем наличие специфичных для Storybook глобальных переменных
    if ('undefined' !== typeof window) {
        return Boolean(
            (window as any).__STORYBOOK_ADDONS_CHANNEL__ ||
            (window as any).__STORYBOOK_PREVIEW__ ||
            window.location?.hostname?.includes('storybook'),
        );
    }

    return false;
};
